import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAbrirNovo } from '../hooks/useAbrirNovo';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuX, LuChevronLeft, LuChevronRight, LuPencil, LuEye, LuTrash2, LuUsers, LuSend,
} from 'react-icons/lu';
import { apiFetch } from '../services/api';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import Select from '../components/Select';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
// UserManagementPage.css é um dos quatro arquivos que declaram
// .management-table, .filters-container e .actions-cell — classes de 15 telas
// ainda não migradas. Esta tela não usa mais nenhuma, mas o import fica: sem
// ele o arquivo sai do bundle. Ver docs/designsystem/estado-do-redesign.md.
import './UserManagementPage.css';
import type { User, Profile, StatusUsuario } from '../types';
import EquipeService, { type Equipe } from '../services/EquipeService';
import { CODIGO_ADMINISTRADOR, ehAdministradorDaEmpresa } from '../config/permissions';

const apiUrl = import.meta.env.VITE_API_URL;
const POR_PAGINA = [25, 50, 100];
const SEM_EQUIPE = '— sem equipe —';

type Modo = 'ver' | 'editar';

interface Rascunho { id: number | null; name: string; email: string; profileId: number; status: 'Ativo' | 'Inativo' }
const VAZIO: Rascunho = { id: null, name: '', email: '', profileId: 0, status: 'Ativo' };

const iniciais = (nome: string) => nome.trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

/* Convite por e-mail (05/10/2026): não existe coluna de status. A API manda
   userDeleted + convitePendente + conviteExpirado e a tela deriva. */
const statusDe = (u: any): StatusUsuario => {
    if (u.userDeleted) return 'Inativo';
    if (u.convitePendente && u.conviteExpirado) return 'Convite expirado';
    if (u.convitePendente) return 'Convite pendente';
    return 'Ativo';
};
const classeStatus = (s: StatusUsuario) =>
    s === 'Ativo' ? 'is-good' : s === 'Convite pendente' ? 'is-open' : s === 'Convite expirado' ? 'is-warn' : 'is-lost';
const aguardandoConvite = (s: StatusUsuario) => s === 'Convite pendente' || s === 'Convite expirado';
const dataCurta = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

/* ─── Filtro em pill (mesmo componente da lista de Contatos) ──── */

const FiltroPill = ({ rotulo, valor, opcoes, onChange }: {
    rotulo: string; valor: string | null;
    opcoes: { valor: string; total: number }[];
    onChange: (v: string | null) => void;
}) => {
    const [aberto, setAberto] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    return (
        <div className="lk-filter" ref={ref}>
            <button type="button" className={`lk-pill ${valor ? 'is-on' : ''}`} aria-expanded={aberto}
                onClick={() => (valor ? onChange(null) : setAberto(v => !v))}>
                {valor ? <>{rotulo}: <b>{valor}</b> <LuX size={12} /></> : <>{rotulo} ▾</>}
            </button>
            {aberto && !valor && (
                <div className="lk-pop" role="menu">
                    {opcoes.length === 0
                        ? <button type="button" disabled>nada para filtrar</button>
                        : opcoes.map(o => (
                            <button key={o.valor} type="button"
                                onClick={() => { onChange(o.valor); setAberto(false); }}>
                                <span>{o.valor}</span><span className="lk-pop-count">{o.total}</span>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
};

/* ─── Tela ────────────────────────────────────────────────────── */

/** Id do usuário logado — para travar o perfil dele mesmo. */
const meuUsuarioId = (): number | null => {
    try {
        return Number(JSON.parse(localStorage.getItem('user') || '{}')?.id) || null;
    } catch {
        return null;
    }
};

const UserManagementPage = () => {
    const celular = useCelular();
    /* Quem tem `gestao_usuarios` sem ser admin não escolhe o perfil
       Administrador. Ninguém, nem o admin, troca o próprio perfil ou se
       desativa (a API devolve 400). O resto
       — perfil com chave que ele não tem, usuário com perfil mais forte — só a
       API sabe calcular: a tela mostra a `mensagem` do 400. */
    const souAdmin = ehAdministradorDaEmpresa();
    const meuId = meuUsuarioId();
    const [usuarios, setUsuarios] = useState<User[]>([]);
    const [perfis, setPerfis] = useState<Profile[]>([]);
    const [equipes, setEquipes] = useState<Equipe[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    /* Recorte de trabalho: os tres filtros desta tela custam caro de remontar
       um por um, e eram justamente os que se perdiam. */
    const [recorte, gravarRecorte] = useRecorte('us:recorte', {
        busca: '', fPerfil: null as string | null, fEquipe: null as string | null,
        fStatus: null as string | null, pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [fPerfil, setFPerfil] = useState<string | null>(recorte.fPerfil);
    const [fEquipe, setFEquipe] = useState<string | null>(recorte.fEquipe);
    const [fStatus, setFStatus] = useState<string | null>(recorte.fStatus);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, fPerfil, fEquipe, fStatus, pagina, porPagina });
    }, [busca, fPerfil, fEquipe, fStatus, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<User | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [senha, setSenha] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});
    const [vinculando, setVinculando] = useState(false);
    const [seletorEquipe, setSeletorEquipe] = useState(false);
    const refSeletor = useRef<HTMLDivElement | null>(null);

    /* ─── Dados ──────────────────────────────────────────────── */

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const [ru, rp] = await Promise.all([
                apiFetch(`${apiUrl}/api/Usuario/BuscarTodos`),
                apiFetch(`${apiUrl}/api/TipoUsuario/BuscarTodos`),
            ]);
            const [du, dp] = await Promise.all([ru.json(), rp.json()]);

            if (du.sucesso) {
                setUsuarios((du.dados || []).map((u: any) => ({
                    id: u.userId,
                    name: u.userName,
                    email: u.userEmail,
                    profileId: u.userTypeuserid,
                    status: statusDe(u),
                    convidadoEm: u.userInvitedAt ?? null,
                })));
            } else aviso.erro(du.mensagem || 'Não foi possível carregar os usuários.');

            if (dp.sucesso) setPerfis(dp.dados || []);
        } catch (e) {
            console.error(e);
            aviso.erro('Não foi possível carregar os usuários.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const carregarEquipes = useCallback(async () => {
        try { setEquipes(await EquipeService.buscarTodas()); } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { carregar(); carregarEquipes(); }, [carregar, carregarEquipes]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, fPerfil, fEquipe, fStatus, porPagina]);

    useEffect(() => {
        if (!seletorEquipe) return;
        const fora = (e: MouseEvent) => {
            if (refSeletor.current && !refSeletor.current.contains(e.target as Node)) setSeletorEquipe(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [seletorEquipe]);

    /* ─── Derivados ──────────────────────────────────────────── */

    const nomePerfil = useCallback(
        (id: number) => perfis.find(p => p.id === id)?.nome ?? '—', [perfis]);

    // Não existe campo de equipe no usuário: deduz varrendo as equipes.
    const equipesDe = useCallback(
        (userId: number | null) => (userId ? equipes.filter(e => e.membroIds?.includes(userId)) : []),
        [equipes]);

    const contar = (pegar: (u: User) => string) => {
        const m = new Map<string, number>();
        usuarios.forEach(u => { const v = pegar(u); if (v) m.set(v, (m.get(v) ?? 0) + 1); });
        return [...m.entries()].map(([valor, total]) => ({ valor, total }))
            .sort((a, b) => b.total - a.total || a.valor.localeCompare(b.valor, 'pt-BR'));
    };

    const opcoesPerfil = useMemo(() => contar(u => nomePerfil(u.profileId)), [usuarios, perfis]);
    const opcoesStatus = useMemo(() => contar(u => u.status), [usuarios]);
    const opcoesEquipe = useMemo(() => {
        const base = equipes.map(e => ({ valor: e.nome, total: e.membroIds?.length ?? 0 }));
        const orfaos = usuarios.filter(u => equipesDe(u.id).length === 0).length;
        return orfaos > 0 ? [{ valor: SEM_EQUIPE, total: orfaos }, ...base] : base;
    }, [equipes, usuarios, equipesDe]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return usuarios.filter(u => {
            if (fPerfil && nomePerfil(u.profileId) !== fPerfil) return false;
            if (fStatus && u.status !== fStatus) return false;
            if (fEquipe) {
                const suas = equipesDe(u.id);
                if (fEquipe === SEM_EQUIPE ? suas.length > 0 : !suas.some(e => e.nome === fEquipe)) return false;
            }
            if (!t) return true;
            return u.name.toLowerCase().includes(t) || u.email.toLowerCase().includes(t);
        });
    }, [usuarios, busca, fPerfil, fEquipe, fStatus, nomePerfil, equipesDe]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    /* ─── Painel ─────────────────────────────────────────────── */

    const abrirVer = (u: User) => { setAberto(u); setModo('ver'); setErros({}); setSeletorEquipe(false); };
    const abrirEdicao = (u: User) => {
        setAberto(u);
        setRascunho({ id: u.id, name: u.name, email: u.email, profileId: u.profileId,
            status: u.status === 'Inativo' ? 'Inativo' : 'Ativo' });
        setSenha(''); setModo('editar'); setErros({});
    };
    /* A paleta do Ctrl+K pediu "Novo usuário": abre o formulário direto. */
    useAbrirNovo(() => abrirNovo());

    const abrirNovo = () => {
        setAberto({ id: null, name: '', email: '', profileId: perfis[0]?.id ?? 0, status: 'Ativo' });
        setRascunho({ ...VAZIO, profileId: perfis[0]?.id ?? 0 });
        setSenha(''); setModo('editar'); setErros({});
    };
    const fechar = () => { setAberto(null); setErros({}); setSeletorEquipe(false); };

    const ehNovo = modo === 'editar' && rascunho.id === null;
    const ehEuMesmo = !ehNovo && meuId !== null && rascunho.id === meuId;

    const validar = () => {
        const e: Record<string, string> = {};
        if (!rascunho.name.trim()) e.name = 'Informe o nome.';
        if (!rascunho.email.trim()) e.email = 'Informe o e-mail.';
        else if (!/^\S+@\S+\.\S+$/.test(rascunho.email.trim())) e.email = 'E-mail inválido.';
        if (!rascunho.profileId) e.profileId = 'Escolha um perfil.';
        // Ao criar não há senha: o convidado cria a própria pelo link do e-mail.
        // Ao editar, "Nova senha" continua opcional.
        if (!ehNovo && senha.length > 0) {
            const ok = senha.length >= 8 && senha.length <= 16
                && /[A-Z]/.test(senha) && /[a-z]/.test(senha) && /[^A-Za-z0-9]/.test(senha);
            if (!ok) e.senha = '8 a 16 caracteres, com maiúscula, minúscula e um caractere especial.';
        }
        setErros(e);
        return Object.keys(e).length === 0;
    };

    const salvar = async () => {
        if (!validar()) return;
        setSalvando(true);
        try {
            const editando = rascunho.id !== null;
            const r = await apiFetch(
                editando ? `${apiUrl}/api/Usuario/Atualizar/${rascunho.id}` : `${apiUrl}/api/Usuario/CriarUsuario`,
                {
                    method: editando ? 'PUT' : 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(editando
                        ? {
                            Nome: rascunho.name.trim(), Email: rascunho.email.trim(),
                            Senha: senha || null, TipoUsuarioId: rascunho.profileId,
                            FilialId: 1, UserDeleted: rascunho.status === 'Inativo',
                        }
                        /* Convite: nome, e-mail e perfil. A senha é da pessoa. */
                        : { Nome: rascunho.name.trim(), Email: rascunho.email.trim(), TipoUsuarioId: rascunho.profileId }),
                });
            const d = await r.json();
            if (!r.ok || !d.sucesso) throw new Error(d.mensagem || 'Falha ao salvar usuário.');

            if (editando) aviso.ok('Usuário atualizado.');
            else aviso.ok(d.mensagem || `Convite enviado para ${rascunho.email.trim()}.`);

            await carregar();
            fechar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar o usuário.'));
        } finally {
            setSalvando(false);
        }
    };

    // O endpoint chama-se Deletar, mas o que ele faz é DESATIVAR — o próprio
    // aviso da tela antiga dizia "usuário desativado". Não existe exclusão dura.
    /* `alvo` opcional porque a desativação tem duas portas: a lixeira da linha
       (que manda o usuário) e o rodapé do painel (que usa o que está em
       edição). O endpoint se chama `Deletar`, mas é soft delete: o usuário
       perde o acesso e o histórico dele fica. */
    const desativar = async (alvo?: User) => {
        const id = alvo?.id ?? rascunho.id;
        const nome = alvo?.name ?? rascunho.name;
        if (!id) return;
        if (!window.confirm(`Desativar "${nome}"? Ele perde o acesso, mas o histórico dele continua.`)) return;
        setSalvando(true);
        try {
            const r = await apiFetch(`${apiUrl}/api/Usuario/Deletar/${id}`, { method: 'DELETE' });
            const d = await r.json();
            if (!r.ok || !d.sucesso) throw new Error(d.mensagem || 'Falha ao desativar.');
            aviso.ok('Usuário desativado.');
            await carregar();
            fechar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível desativar o usuário.'));
        } finally {
            setSalvando(false);
        }
    };

    /* Token novo, e-mail novo. O link anterior deixa de valer na hora. */
    const reenviarConvite = async (u: User) => {
        if (!u.id) return;
        setSalvando(true);
        try {
            const r = await apiFetch(`${apiUrl}/api/Usuario/ReenviarConvite/${u.id}`, { method: 'POST' });
            const d = await r.json();
            if (!r.ok || !d.sucesso) throw new Error(d.mensagem || 'Falha ao reenviar o convite.');
            aviso.ok(d.mensagem || `Convite reenviado para ${u.email}.`);
            await carregar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível reenviar o convite.'));
        } finally {
            setSalvando(false);
        }
    };

    /* ─── O plus: vincular a equipe ──────────────────────────── */

    /** Relê as equipes antes de salvar: o DTO manda o registro inteiro, então
     *  trabalhar com lista velha apagaria alteração de outra pessoa. */
    const mexerNaEquipe = async (equipeId: number, mudar: (ids: number[]) => number[], msg: string) => {
        const userId = aberto?.id;
        if (!userId) return;
        setVinculando(true);
        try {
            const atuais = await EquipeService.buscarTodas();
            const alvo = atuais.find(e => e.id === equipeId);
            if (!alvo) {
                aviso.atencao('Essa equipe não existe mais.');
                setEquipes(atuais);
                return;
            }
            await EquipeService.atualizar(alvo.id, {
                nome: alvo.nome,
                liderId: alvo.liderId,
                membroIds: mudar(alvo.membroIds ?? []),
            });
            aviso.ok(msg);
            await carregarEquipes();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível atualizar a equipe.'));
        } finally {
            setVinculando(false);
            setSeletorEquipe(false);
        }
    };

    const entrarNaEquipe = (e: Equipe) =>
        mexerNaEquipe(e.id, ids => (ids.includes(aberto!.id!) ? ids : [...ids, aberto!.id!]),
            `Adicionado a ${e.nome}.`);

    const sairDaEquipe = (e: Equipe) =>
        mexerNaEquipe(e.id, ids => ids.filter(i => i !== aberto!.id), `Removido de ${e.nome}.`);

    const minhasEquipes = equipesDe(aberto?.id ?? null);
    const equipesDisponiveis = equipes.filter(e => !e.membroIds?.includes(aberto?.id ?? -1));

    /* ─── Render ─────────────────────────────────────────────── */

    const COLUNAS = '1.2fr 1.4fr .8fr 1.2fr .6fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {usuarios.length === 0 ? 'Nenhum usuário cadastrado' : 'Nada com esses filtros'}
            </div>
            <p className="lk-blank-text">
                {usuarios.length === 0
                    ? 'Convide quem vai usar a plataforma: a pessoa recebe um e-mail e cria a própria senha.'
                    : 'Ajuste a busca ou limpe os filtros.'}
            </p>
            {usuarios.length === 0
                ? <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Convidar usuário</button>
                : <button type="button" className="lk-btn"
                    onClick={() => { setBusca(''); setFPerfil(null); setFEquipe(null); setFStatus(null); }}>
                    Limpar filtros</button>}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">{inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}</span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} /></button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} /></button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Usuários"
                subtitle="Quem tem acesso à plataforma"
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && usuarios.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Convidar usuário
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome ou e-mail…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <FiltroPill rotulo="Perfil" valor={fPerfil} opcoes={opcoesPerfil} onChange={setFPerfil} />
                    <FiltroPill rotulo="Equipe" valor={fEquipe} opcoes={opcoesEquipe} onChange={setFEquipe} />
                    <FiltroPill rotulo="Status" valor={fStatus} opcoes={opcoesStatus} onChange={setFStatus} />
                    <span className="lk-count">
                        {usuarios.length} {usuarios.length === 1 ? 'usuário' : 'usuários'}
                        {filtrados.length !== usuarios.length && ` · ${filtrados.length} no filtro`}
                    </span>
                </div>

                {/* Celular: a grade de 6 colunas não cabe em 358px, então a linha
                    vira cartão — quem é a pessoa, por onde ela entra e o que pode
                    fazer. Equipes ficam no painel, um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(u => (
                                    <ListCard
                                        key={u.id ?? u.email}
                                        avatar={<span className="lk-ava" aria-hidden="true">{iniciais(u.name)}</span>}
                                        titulo={u.name}
                                        apoio={u.email}
                                        estado={
                                            <>
                                                <span className="lk-tag">{nomePerfil(u.profileId)}</span>
                                                <span className={`rp-badge ${classeStatus(u.status)}`}>
                                                    {u.status}
                                                </span>
                                            </>
                                        }
                                        selected={aberto?.id === u.id}
                                        label={`Abrir ${u.name}`}
                                        onOpen={() => abrirVer(u)}
                                        acoes={
                                            <>
                                                {aguardandoConvite(u.status) && (
                                                    <RowAction icon={<LuSend size={15} />} label="Reenviar convite"
                                                        disabled={salvando} onClick={() => reenviarConvite(u)} />
                                                )}
                                                <RowAction icon={<LuTrash2 size={15} />}
                                                    label={aguardandoConvite(u.status) ? 'Cancelar convite' : 'Desativar usuário'} danger
                                                    onClick={() => desativar(u)} />
                                            </>
                                        }
                                    />
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Nome</div><div className="lk-head">E-mail</div>
                            <div className="lk-head">Perfil</div><div className="lk-head">Equipes</div>
                            <div className="lk-head">Status</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(u => {
                                    const suas = equipesDe(u.id);
                                    return (
                                        <ListRow key={u.id ?? u.email} columns={COLUNAS}
                                            selected={aberto?.id === u.id}>
                                            <span className="lk-cell lk-strong"><span>{u.name}</span></span>
                                            <span className="lk-cell lk-muted"><span>{u.email}</span></span>
                                            <span className="lk-cell"><span className="lk-tag">{nomePerfil(u.profileId)}</span></span>
                                            <span className="lk-cell">
                                                {suas.length === 0
                                                    ? <span className="lk-empty-value">sem equipe</span>
                                                    : <span>
                                                        <span className="lk-tag">{suas[0].nome}</span>
                                                        {suas.length > 1 && <span className="lk-tag">+{suas.length - 1}</span>}
                                                      </span>}
                                            </span>
                                            <span className="lk-cell">
                                                <span className={`rp-badge ${classeStatus(u.status)}`}>
                                                    {u.status}
                                                </span>
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver usuário"
                                                    onClick={() => abrirVer(u)} />
                                                {aguardandoConvite(u.status) && (
                                                    <RowAction icon={<LuSend size={14} />} label="Reenviar convite"
                                                        disabled={salvando} onClick={() => reenviarConvite(u)} />
                                                )}
                                                <RowAction icon={<LuTrash2 size={14} />}
                                                    label={aguardandoConvite(u.status) ? 'Cancelar convite' : 'Desativar usuário'} danger
                                                    onClick={() => desativar(u)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}

                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                initials={iniciais(modo === 'editar' ? (rascunho.name || 'Novo') : (aberto?.name ?? ''))}
                title={modo === 'editar'
                    ? (rascunho.name.trim() || (ehNovo ? 'Convidar usuário' : 'Usuário'))
                    : (aberto?.name ?? '')}
                subtitle={aberto ? `Usuário · ${aberto.status.toLowerCase()}` : 'Usuário'}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={
                    <>
                        {!ehNovo && rascunho.status === 'Ativo' && (
                            <button type="button" className="rp-btn is-danger" onClick={() => desativar()} disabled={salvando}>
                                {aberto && aguardandoConvite(aberto.status) ? 'Cancelar convite' : 'Desativar usuário'}
                            </button>
                        )}
                        <button type="button" className="rp-btn"
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))} disabled={salvando}>
                            Cancelar
                        </button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? (ehNovo ? 'Enviando…' : 'Salvando…') : (ehNovo ? 'Enviar convite' : 'Salvar')}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && (
                    <>
                        <PanelSection title="Acesso">
                            <div className="rp-kv">
                                <span className="rp-k">E-mail</span><span className="rp-v">{aberto.email}</span>
                                <span className="rp-k">Perfil</span>
                                <span className="rp-v"><span className="rp-tag">{nomePerfil(aberto.profileId)}</span></span>
                                <span className="rp-k">Status</span>
                                <span className="rp-v">
                                    <span className={`rp-badge ${classeStatus(aberto.status)}`}>
                                        {aberto.status}
                                    </span>
                                </span>
                                {aguardandoConvite(aberto.status) && (
                                    <>
                                        <span className="rp-k">Convidado em</span>
                                        <span className="rp-v">{dataCurta(aberto.convidadoEm) || '—'}</span>
                                    </>
                                )}
                            </div>
                            {aguardandoConvite(aberto.status) && (
                                <>
                                    <p className="rp-none">
                                        {aberto.status === 'Convite expirado'
                                            ? 'O link venceu. Reenvie para a pessoa receber um novo.'
                                            : 'A pessoa ainda não criou a senha. O link do e-mail vale 7 dias.'}
                                    </p>
                                    <button type="button" className="rp-add" disabled={salvando}
                                        onClick={() => reenviarConvite(aberto)}>
                                        <LuSend size={13} /> {salvando ? 'Enviando…' : 'Reenviar convite'}
                                    </button>
                                </>
                            )}
                        </PanelSection>

                        {/* O plus. Vive no modo ver e é aplicado na hora: como campo do
                            formulário, o Salvar dispararia N atualizações de equipe e uma
                            falha no meio deixaria o registro pela metade. */}
                        <PanelSection title={`Equipes${minhasEquipes.length ? ` · ${minhasEquipes.length}` : ''}`}>
                            {minhasEquipes.length === 0
                                ? <p className="rp-none">Não está em nenhuma equipe.</p>
                                : minhasEquipes.map(e => {
                                    const ehLider = e.liderId === aberto.id;
                                    return (
                                        <div className="rp-file" key={e.id}>
                                            <span className="rp-file-ico"><LuUsers size={12} /></span>
                                            <span className="rp-file-name">
                                                {e.nome}
                                                {ehLider && <span className="rp-badge is-open" style={{ marginLeft: 6 }}>líder</span>}
                                            </span>
                                            <button
                                                type="button"
                                                className="rp-file-act is-danger"
                                                onClick={() => sairDaEquipe(e)}
                                                disabled={ehLider || vinculando}
                                                title={ehLider
                                                    ? 'É líder desta equipe — troque o líder antes de removê-lo'
                                                    : 'Remover da equipe'}
                                                aria-label="Remover da equipe"
                                            >
                                                <LuX size={13} />
                                            </button>
                                        </div>
                                    );
                                })}

                            <div className="lk-filter" ref={refSeletor} style={{ display: 'block' }}>
                                <button type="button" className="rp-add" disabled={vinculando || !aberto.id}
                                    onClick={() => setSeletorEquipe(v => !v)}>
                                    <LuPlus size={13} /> {vinculando ? 'Salvando…' : 'Adicionar a uma equipe'}
                                </button>
                                {seletorEquipe && (
                                    <div className="lk-pop" role="menu" style={{ position: 'static', marginTop: 8 }}>
                                        {equipesDisponiveis.length === 0
                                            ? <button type="button" disabled>já está em todas as equipes</button>
                                            : equipesDisponiveis.map(e => (
                                                <button key={e.id} type="button" onClick={() => entrarNaEquipe(e)}>
                                                    <span>{e.nome}</span>
                                                    <span className="lk-pop-count">{e.membroIds?.length ?? 0} {(e.membroIds?.length ?? 0) === 1 ? 'membro' : 'membros'}</span>
                                                </button>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </PanelSection>
                    </>
                )}

                {modo === 'editar' && (
                    <PanelSection title="Acesso">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="us-nome">Nome</label>
                            <input id="us-nome" className={`rp-input ${erros.name ? 'has-error' : ''}`} autoFocus
                                value={rascunho.name} onChange={e => setRascunho(r => ({ ...r, name: e.target.value }))} />
                            {erros.name && <p className="rp-error">{erros.name}</p>}
                        </div>
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="us-mail">E-mail</label>
                            <input id="us-mail" type="email" className={`rp-input ${erros.email ? 'has-error' : ''}`}
                                value={rascunho.email} onChange={e => setRascunho(r => ({ ...r, email: e.target.value }))} />
                            {erros.email && <p className="rp-error">{erros.email}</p>}
                        </div>
                        <div className="rp-two">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="us-perfil">Perfil</label>
                                <Select id="us-perfil" hasError={!!erros.profileId}
                                    placeholder="Escolha…" disabled={ehEuMesmo}
                                    value={rascunho.profileId ? String(rascunho.profileId) : ''}
                                    /* O perfil Administrador some para quem não é admin — a não
                                       ser que já seja o do usuário aberto, senão o campo ficaria vazio. */
                                    options={perfis
                                        .filter(p => souAdmin || Number(p.codigo) !== CODIGO_ADMINISTRADOR
                                            || p.id === rascunho.profileId)
                                        .map(p => ({ value: String(p.id), label: p.nome }))}
                                    onChange={v => setRascunho(r => ({ ...r, profileId: Number(v) }))} />
                                {erros.profileId && <p className="rp-error">{erros.profileId}</p>}
                            </div>
                            {!ehNovo && (
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="us-status">Status</label>
                                    <Select id="us-status" value={rascunho.status} disabled={ehEuMesmo}
                                        options={[{ value: 'Ativo', label: 'Ativo' }, { value: 'Inativo', label: 'Inativo' }]}
                                        onChange={v => setRascunho(r => ({ ...r, status: v as 'Ativo' | 'Inativo' }))} />
                                </div>
                            )}
                        </div>
                        {/* A API recusa os dois: ninguém troca o próprio perfil nem se desativa. */}
                        {ehEuMesmo && (
                            <p className="rp-toggle-hint">Seu perfil e seu status só podem ser alterados por outro administrador.</p>
                        )}
                        {ehNovo ? (
                            /* Convite por e-mail: ninguém digita a senha de ninguém. */
                            <p className="rp-none">
                                A pessoa recebe um e-mail com um link para criar a própria senha. O link vale 7 dias.
                            </p>
                        ) : (
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="us-senha">Nova senha</label>
                                <input id="us-senha" type="password" className={`rp-input ${erros.senha ? 'has-error' : ''}`}
                                    placeholder="Deixe em branco para não alterar"
                                    value={senha} onChange={e => setSenha(e.target.value)} />
                                {erros.senha && <p className="rp-error">{erros.senha}</p>}
                            </div>
                        )}
                    </PanelSection>
                )}

                {/* Histórico (fase 4): convite, perfil, situação, senha (sem o valor). */}
                {modo === 'ver' && aberto?.id && (
                    <SecaoHistorico entidade="usuario" id={aberto.id} />
                )}
            </RecordPanel>

        </div>
    );
};

export default UserManagementPage;
